import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { Button, Progress, Space, Table, message, Tooltip, Modal, Tag, Empty } from 'antd';
import { DownloadOutlined, DeleteOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ISO6391 from 'iso-639-1';
import './aiAssistant.scss';
import MeetingSummaryForm from './meetingSummaryForm';
import TranslationForm from './translationForm';
import SolarCalculationForm from './solarCalculationForm';
import AuthContext from '../../contexts/authProvider';
import { getAIToolHistoryByUserEmail, deleteAIToolHistory, AI_ASSISTANT_TYPES, AI_ASSISTANT_FILE_TYPES, getEventStatus, downloadTaskResult } from '../../api/aiTools';
import { formatTimestamp, getDocumentType } from '../../utils/functionTool';
import { useNavigate } from 'react-router-dom';
import LoadingAnimation from '../../components/LoadingAnimation';

const getTypeConfig = (type, t) => {
  const configs = {
    [AI_ASSISTANT_TYPES.MEETING_SUMMARY]: {
      title: t('aiAssistant.meetingSummary.title'),
      processingTitle: t('aiAssistant.meetingSummary.processingTitle'),
      completedTitle: t('aiAssistant.meetingSummary.completedTitle'),
      downloadButtons: [
        { text: t('aiAssistant.meetingSummary.downloadTranscript'), key: 'transcript' },
        { text: t('aiAssistant.meetingSummary.downloadAnalysis'), key: 'analysis' }
      ]
    },
    [AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION]: {
      title: t('aiAssistant.documentTranslation.title'),
      processingTitle: t('aiAssistant.documentTranslation.processingTitle'),
      completedTitle: t('aiAssistant.documentTranslation.completedTitle'),
      downloadButtons: [
        { text: t('aiAssistant.documentTranslation.downloadTranslation'), key: 'translation' }
      ]
    },
    [AI_ASSISTANT_TYPES.SOLAR_CALCULATION]: {
      title: t('aiAssistant.solarCalculation.title'),
      processingTitle: t('aiAssistant.solarCalculation.processingTitle'),
      completedTitle: t('aiAssistant.solarCalculation.completedTitle'),
      downloadButtons: []
    }
  };
  return configs[type];
};

const getLanguageDisplay = (languageCode, t) => {
  if (languageCode === 'zh-TW') {
    return t('aiAssistant.common.traditionalChinese');
  }
  const code = languageCode.split('-')[0];
  const name = ISO6391.getName(code);
  const nativeName = ISO6391.getNativeName(code);
  return nativeName ? `${name} (${nativeName})` : name;
};

const AIAssistant = ({ type = AI_ASSISTANT_TYPES.MEETING_SUMMARY }) => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const config = getTypeConfig(type, t);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const dataRef = useRef(data);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchData = useCallback(async (currentPage = 1, isLoadMore = false) => {
    setLoading(true);
    try {
      const response = await getAIToolHistoryByUserEmail({
        page: currentPage,
        pageSize,
        userEmail: user?.email,
        eventType: type
      });
      
      // 處理資料分組和排序
      const processingItems = [];
      const completedItems = [];
      
      response.forEach(item => {
        // 使用新的資料結構判斷處理狀態
        const isProcessing = item.event_status?.event_status === 'PROCESSING' || 
                           item.event_status?.event_status === 'UNKNOWN' ||
                           item.event_status?.event_status === 'STARTED' ||
                           item.event_status?.event_status === undefined;
        
        const processedItem = {
          ...item,
          // 將新的資料結構映射到原有的資料結構
          task: {
            task_progress: {
              status: item.event_status?.event_status,
              message: item.event_status?.event_message,
              progress: item.event_status?.event_progress
            },
            task_result: item.event_result
          },
          status: isProcessing ? 'PROCESSING' : 'COMPLETED',
          group: isProcessing ? t('aiAssistant.common.processingGroup') : t('aiAssistant.common.completedGroup')
        };
        
        if (isProcessing) {
          processingItems.push(processedItem);
        } else {
          completedItems.push(processedItem);
        }
      });
      
      // 已完成項目按時間排序
      completedItems.sort((a, b) => new Date(b.event_timestamp) - new Date(a.event_timestamp));
      
      // 合併處理中和已完成的項目
      const processedData = [...processingItems, ...completedItems];
      
      setData(prev => {
        // 檢查先前的 items 中是否有已標記為失敗 (task_progress.status === 'FAILURE') 的項目
        // 若有，則保留失敗狀態，避免 fetchData 回傳的新資料覆蓋本地的失敗資料
        const mergedData = processedData.map(item => {
          const localFailure = prev.find(p => p.event_id === item.event_id && p.task?.task_progress?.status === 'FAILURE');
          return localFailure ? localFailure : item;
        });
        return isLoadMore ? [...prev, ...mergedData] : mergedData;
      });
      setHasMore(processedData.length === pageSize);
    } catch (error) {
      message.error(t('aiAssistant.errors.fetchDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [type, user?.email, t]);

  const updateProcessingItems = useCallback(async () => {
    const processingItems = dataRef.current.filter(item => item.status === 'PROCESSING');
    if (processingItems.length === 0) return;

    const updatedItems = [];
    // 逐一處理每個 processing 項目，確保拿到 response 或 timeout 後再處理下一項
    for (const item of processingItems) {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 30000);
      });

      try {
        // 等待收到 getEventStatus 的回應或超時後才會繼續
        const statusData = await Promise.race([
          getEventStatus(item.event_id, item.task?.task_progress?.progress),
          timeoutPromise
        ]);

        const newStatus = statusData?.event_status;
        let updatedItem;
        if (newStatus === 'FAILURE') {
          updatedItem = {
            ...item,
            event_status: statusData,
            task: {
              ...item.task,
              task_progress: {
                status: 'FAILURE',
                message: statusData?.event_message,
                progress: statusData?.event_progress
              }
            },
            status: 'COMPLETED',
            group: t('aiAssistant.common.completedGroup')
          };
        } else {
          updatedItem = {
            ...item,
            task: {
              ...item.task,
              task_progress: {
                status: newStatus,
                message: statusData?.event_message,
                progress: statusData?.event_progress
              }
            },
            status: newStatus
          };
        }
        updatedItems.push(updatedItem);
      } catch (error) {
        // 若超時則保留原狀態，並記錄 warning
        if (error.message === 'Request timeout') {
          console.warn(`Request timeout for event ${item.event_id}`);
          updatedItems.push(item);
        } else {
          throw error;
        }
      }
    }

    setData(prevData => {
      const newData = [...prevData];
      updatedItems.forEach(updatedItem => {
        const index = newData.findIndex(item => item.event_id === updatedItem.event_id);
        if (index !== -1) {
          newData[index] = updatedItem;
        }
      });
      return newData;
    });

    // 如果所有處理中的項目均不再處於 PROCESSING 狀態則重新抓取資料
    if (updatedItems.every(item => item.status !== 'PROCESSING')) {
      fetchData(1, false);
    }
  }, [fetchData, t]);

  useEffect(() => {
    // 重置狀態並獲取數據
    setData([]);
    setPage(1);
    setHasMore(true);
    fetchData(1, false);
  }, [fetchData]);

  useEffect(() => {
    let timerId;

    const runUpdate = async () => {
      await updateProcessingItems();
      timerId = setTimeout(runUpdate, 5000);
    };

    runUpdate();

    return () => {
      clearTimeout(timerId);
    };
  }, [updateProcessingItems]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(nextPage, true);
  };

  const handleCancel = (record) => {
    Modal.confirm({
      title: t('aiAssistant.actions.confirmCancel'),
      content: t('aiAssistant.actions.confirmCancelMessage'),
      okText: t('aiAssistant.actions.confirm'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: async () => {
        await deleteAIToolHistory(record.event_id);
        message.success(t('aiAssistant.actions.cancelSuccess'));
        fetchData();
      }
    });
  };

  const handleDiscussion = async (record) => {
    const file_types = type === AI_ASSISTANT_TYPES.MEETING_SUMMARY
    ? [AI_ASSISTANT_FILE_TYPES.MATERIAL, AI_ASSISTANT_FILE_TYPES.MEDIA, AI_ASSISTANT_FILE_TYPES.SUMMARY]
    : [AI_ASSISTANT_FILE_TYPES.MATERIAL, AI_ASSISTANT_FILE_TYPES.TRANSLATE, AI_ASSISTANT_FILE_TYPES.DICTIONARY];
    // Get all file info based on file types
    const fileInfos = file_types.map(fileType => {
      const sourceKey = `source_${fileType}`;
      return record.event_result?.[sourceKey];
    }).filter(Boolean);

    if (!fileInfos.length) {
      message.error(t('aiAssistant.errors.cannotGetFileInfo'));
      return;
    }

    // Convert each file info to file data
    const filesData = fileInfos.map(fileInfo => ({
      source_source: 'upload',
      source_name: fileInfo.source_name,
      source_type: 'text/plain', 
      source_url: fileInfo.source_url,
      source_content: fileInfo.source_content,
      source_checksum: fileInfo.source_checksum,
      dispatchStatus: 'pending',
      status: 1,
      isUploading: true,
      needsDispatch: true
    }));

    // Navigate to base askquestion path with file data
    navigate('/askquestion', { 
      state: { 
        tempUploadedFiles: filesData
      } 
    });
  };

  const getColumns = (isCompleted) => [
    {
      title: type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION
        ? t('aiAssistant.solarCalculation.siteInfo')
        : t('aiAssistant.common.fileName'),
      width: type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION ? '35%' : '25%',
      dataIndex: ['data', `${type === AI_ASSISTANT_TYPES.MEETING_SUMMARY ? 'media_path' : 'material_path'}`],
      key: 'media_name',
      render: (defaultPathValue, record) => {
        const cellData = record?.data;

        // Solar: show all input parameters (2 lines with i18n labels)
        if (type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION && cellData) {
          const f = (key) => t(`aiAssistant.solarCalculation.form.fields.${key}.label`);
          const dateRange = cellData.start_date || cellData.end_date
            ? `${cellData.start_date || '—'} ~ ${cellData.end_date || '—'}`
            : t('aiAssistant.solarCalculation.siteInfoAllData');
          return (
            <div style={{ fontSize: 13, lineHeight: '1.6' }}>
              <div>{f('lat')}: {cellData.lat}, {f('lon')}: {cellData.lon}</div>
              <div>{f('azimuth')}: {cellData.azimuth ?? 0}°, {f('slope')}: {cellData.slope ?? 10}°, {f('capacity_kw')}: {cellData.capacity_kw ?? 1} kWp</div>
              <div style={{ color: '#888', fontSize: 12 }}><strong>{t('aiAssistant.solarCalculation.siteInfoDateRange')}:</strong> {dateRange}</div>
            </div>
          );
        }

        if (cellData?.media_name) {
          return cellData.media_name;
        }

        if (defaultPathValue && typeof defaultPathValue === 'string') {
          return defaultPathValue.split('/').pop();
        }

        if (defaultPathValue) {
          return String(defaultPathValue);
        }

        return '-';
      }
    },
    {
      title: isCompleted ? '' : t('aiAssistant.common.status'),
      width: type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION ? '35%' : '50%',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (status === 'PROCESSING') {
          return (
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                {type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION && (
                  <Tag color="blue">{getLanguageDisplay(record.data.target_language, t)}</Tag>
                )}
                <Tooltip title={record.task?.task_progress?.message || t('aiAssistant.common.processing')}>
                  <Progress 
                    percent={record.task?.task_progress?.progress || 0} 
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                </Tooltip>
              </Space>
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => handleCancel(record)}
              >
                {t('aiAssistant.actions.cancel')}
              </Button>
            </Space>
          );
        }
        const detailStatus = record.event_status?.event_status;
        const isFailed = detailStatus === 'FAILURE' || detailStatus === 'ERROR';
        return (
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Tooltip title={record?.task?.task_progress?.message || record.event_status?.event_message || t('aiAssistant.common.processing')}>
                <Tag color={isFailed ? 'red' : 'green'}>{detailStatus}</Tag>
              </Tooltip>
              {type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION && (
                <Tag color="blue">{getLanguageDisplay(record.data.target_language, t)}</Tag>
              )}
            </Space>
            <Space>
              {!isFailed && (
                <>
                  {type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION ? (
                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleDownloadSolarResult(record)}>
                      {t('aiAssistant.solarCalculation.downloadResult')}
                    </Button>
                  ) : type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION ? (
                    <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleDownload(record, AI_ASSISTANT_FILE_TYPES.TRANSLATE)}>
                      {t('aiAssistant.documentTranslation.downloadTranslation')}
                    </Button>
                  ) : (
                    <>
                      <Tag color="blue">{getDocumentType(record.data?.output_format)}</Tag>
                      <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleDownload(record, AI_ASSISTANT_FILE_TYPES.MEDIA)}>
                        {t('aiAssistant.meetingSummary.downloadTranscript')}
                      </Button>
                      <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleDownload(record, AI_ASSISTANT_FILE_TYPES.SUMMARY)}>
                        {t('aiAssistant.meetingSummary.downloadAnalysis')}
                      </Button>
                    </>
                  )}
                  {type !== AI_ASSISTANT_TYPES.SOLAR_CALCULATION && (
                    <Button type="primary" icon={<MessageOutlined />} onClick={() => handleDiscussion(record)}>
                      {t('aiAssistant.common.discussion')}
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Space>
        );
      }
    },
    {
      title: t('aiAssistant.common.createTime'),
      width: '25%',
      dataIndex: 'event_timestamp',
      key: 'created_at',
      render: (timestamp) => formatTimestamp(timestamp)
    }
  ];

  const buildSolarFileName = (data) => {
    const parts = [
      'solar',
      `lat${data?.lat}`,
      `lon${data?.lon}`,
      `az${data?.azimuth ?? 0}`,
      `sl${data?.slope ?? 10}`,
      `${data?.capacity_kw ?? 1}kWp`,
    ];
    if (data?.start_date) parts.push(data.start_date.replace(/[\s:]/g, ''));
    if (data?.end_date) parts.push(data.end_date.replace(/[\s:]/g, ''));
    return `${parts.join('_')}.json`;
  };

  const handleDownloadSolarResult = async (record) => {
    try {
      const result = record.event_result;
      const fileName = buildSolarFileName(record.data);

      // Path A: S3 presigned URL download (new records with source_solar)
      if (result?.source_solar) {
        const presignedUrl = await downloadTaskResult(record.event_id, 'solar');
        if (presignedUrl) {
          const resp = await fetch(presignedUrl);
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
      }

      // Path B: Fallback — direct blob download (legacy or inline result)
      const fallbackResult = result || record.task?.task_result;
      if (!fallbackResult) {
        message.error(t('aiAssistant.errors.fileContentNotExists'));
        return;
      }
      const blob = new Blob([JSON.stringify(fallbackResult, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(t('aiAssistant.errors.downloadFailed') + error.message);
    }
  };

  const handleDownload = async (record, fileType) => {
    try {
      const sourceKey = `source_${fileType}`;
      const fileInfo = record.event_result?.[sourceKey];
      
      if (!fileInfo) {
        throw new Error(t('aiAssistant.errors.fileInfoNotExists'));
      }

      const file_content = fileInfo.source_content;
      const file_url = fileInfo.source_url;
      const original_file_name = fileInfo.source_name;

      // 目前只有逐字稿需要轉換副檔名
      const file_name = fileType === AI_ASSISTANT_FILE_TYPES.MEDIA 
        ? original_file_name.replace(/\.[^/.]+$/, '.txt') 
        : original_file_name;

      if (!file_url && !file_content) {
        throw new Error(t('aiAssistant.errors.fileContentNotExists'));
      }

      if (fileType === AI_ASSISTANT_FILE_TYPES.SUMMARY || fileType === AI_ASSISTANT_FILE_TYPES.TRANSLATE) {
        // 分析檔案直接使用 S3 下載連結
        window.open(file_url, '_blank');
      } else {
        // 逐字稿使用 blob 下載
        const blob = new Blob([file_content], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file_name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      message.error(t('aiAssistant.errors.downloadFailed') + error.message);
    }
  };

  return (
    <div className="ai-assistant-container">
      <h2>{config.title}</h2>
      
      <div className="section-container">
        <div className="section-header processing">
          <h3>{config.processingTitle}</h3>
        </div>
        <div className="section-content">
          <Table
            columns={getColumns(false)}
            dataSource={data.filter(item => item.status === 'PROCESSING')}
            rowKey="event_id"
            pagination={false}
            loading={{ spinning: loading, indicator: <LoadingAnimation /> }}
            locale={{
              emptyText: loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('aiAssistant.common.loading')} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                  type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION ? t('aiAssistant.solarCalculation.noProcessingItems')
                  : type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION ? t('aiAssistant.documentTranslation.noProcessingItems')
                  : t('aiAssistant.meetingSummary.noProcessingItems')
                } />
              )
            }}
            rowClassName="processing-row"
          />
        </div>
      </div>

      <div className="section-container">
        <div className="section-header completed">
          <h3>{config.completedTitle}</h3>
        </div>
        <div className="section-content">
          <Table
            columns={getColumns(true)}
            dataSource={data.filter(item => item.status === 'COMPLETED')}
            rowKey="event_id"
            pagination={false}
            loading={{ spinning: loading, indicator: <LoadingAnimation /> }}
            locale={{
              emptyText: loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('aiAssistant.common.loading')} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                  type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION ? t('aiAssistant.solarCalculation.noCompletedItems')
                  : type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION ? t('aiAssistant.documentTranslation.noCompletedItems')
                  : t('aiAssistant.meetingSummary.noCompletedItems')
                } />
              )
            }}
            rowClassName="completed-row"
          />
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <Button onClick={handleLoadMore} loading={loading}>
                {t('aiAssistant.common.loadMore')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Button type="primary" className="ai-assistant-add-button" onClick={() => setIsModalVisible(true)}>
        {t('aiAssistant.common.addNew')}
      </Button>

      {type === AI_ASSISTANT_TYPES.MEETING_SUMMARY && (
        <MeetingSummaryForm
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onAfterSubmit={(result) => {
            setIsModalVisible(false);
            fetchData();
          }}
          user={user}
        />
      )}
      {type === AI_ASSISTANT_TYPES.DOCUMENT_TRANSLATION && (
        <TranslationForm
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onAfterSubmit={(result) => {
            console.log('Form values:', result);
            setIsModalVisible(false);
            fetchData();
          }}
          user={user}
        />
      )}
      {type === AI_ASSISTANT_TYPES.SOLAR_CALCULATION && (
        <SolarCalculationForm
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onAfterSubmit={(result) => {
            setIsModalVisible(false);
            fetchData();
          }}
          user={user}
        />
      )}
    </div>
  );
};

export default AIAssistant;
